import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useSearchContext } from '@atlas/core';

import { SEARCH_DEBOUNCE_TIMER, searchQuerySymbol } from 'common/constants';
import { useSearch } from 'hooks/useSearch';

import { useSearchInputState } from './context/InputProvider';
import SearchInput from './Input';

const StandardSearchInput = () => {
  const { search } = useLocation();
  const { push } = useHistory();

  const { initialized, onSearch } = useSearchContext();
  const { isValidPrompt, searchString } = useSearch();
  const { setFocused } = useSearchInputState();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const onSearchHandler = useCallback(
    e => {
      const params = new URLSearchParams(searchParams);
      params.set(searchQuerySymbol, e.target.value);

      if (!params.get(searchQuerySymbol)) {
        params.delete(searchQuerySymbol);
      }

      push({ search: params.toString() });
    },
    [push, searchParams],
  );

  const onCancelSearchHandler = useCallback(() => {
    searchParams.delete(searchQuerySymbol);
    push({ search: searchParams.toString() });
    setFocused(false);
  }, [push, searchParams, setFocused]);

  const onCleanSearchHandler = useCallback(() => {
    searchParams.delete(searchQuerySymbol);
    push({ search: searchParams.toString() });
  }, [push, searchParams]);

  useEffect(() => {
    if (!searchString || initialized) return;
    setFocused(true);
  }, [searchString, setFocused, initialized]);

  useEffect(() => {
    if (!isValidPrompt) return;

    const handler = setTimeout(() => {
      onSearch(searchString);
    }, SEARCH_DEBOUNCE_TIMER);

    return () => clearTimeout(handler);
  }, [isValidPrompt, searchString]);

  useEffect(() => {
    return () => {
      setFocused(false);
    };
  }, [setFocused]);

  return (
    <SearchInput
      onCancel={onCancelSearchHandler}
      onClean={onCleanSearchHandler}
      onSearch={onSearchHandler}
    />
  );
};

export default StandardSearchInput;
